import { useQuery } from '@tanstack/react-query';
import { TrendingUp, AlertCircle, CheckCircle2 } from 'lucide-react';
import { api } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { cn } from '@/lib/cn';

type Row = {
  id: string;
  partida_codigo: string;
  partida_nombre: string;
  unidad_nombre?: string;
  techo: number;
  comprometido: number;
  devengado: number;
  pagado: number;
  disponible: number;
};

export function PresupuestoEgresosPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['presupuesto', 'egresos'],
    queryFn: () => api<Row[]>('/presupuesto/egresos'),
  });

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState msg={(error as Error).message} />;
  if (!data || data.length === 0) return <EmptyState />;

  const total = data.reduce(
    (acc, r) => {
      acc.techo += r.techo;
      acc.comprometido += r.comprometido;
      acc.devengado += r.devengado;
      acc.pagado += r.pagado;
      acc.disponible += r.disponible;
      return acc;
    },
    { techo: 0, comprometido: 0, devengado: 0, pagado: 0, disponible: 0 }
  );
  const porcGlobal = total.techo > 0 ? (total.comprometido / total.techo) * 100 : 0;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-mute">
          Presupuesto · Egresos · Ejercicio 2026
        </p>
        <h1 className="mt-1 font-display text-[28px] font-bold tracking-tight text-ink">
          Techo presupuestal por partida
        </h1>
        <p className="mt-1 text-[14px] text-ink-mute">
          Armonizado con el Clasificador por Objeto del Gasto (COG / CONAC). Cada afectación genera póliza automática vía la matriz Art. 41 LGCG.
        </p>
      </div>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Techo total"    value={fmt.money(total.techo)}        accent="brand" />
        <StatCard label="Comprometido"   value={fmt.money(total.comprometido)} sub={`${porcGlobal.toFixed(1)}% del techo`} accent="warn" />
        <StatCard label="Devengado"      value={fmt.money(total.devengado)}    accent="neutral" />
        <StatCard label="Disponible"     value={fmt.money(total.disponible)}   accent="success" />
      </section>

      <Card>
        <CardHeader title="Partidas" subtitle={`${data.length} combinaciones (partida × unidad × fuente)`} />
        <CardBody className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full text-[13px]">
              <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                <tr>
                  <th className="px-5 py-3">Partida</th>
                  <th className="px-5 py-3">Descripción</th>
                  <th className="px-5 py-3 text-right">Techo</th>
                  <th className="px-5 py-3 text-right">Comprometido</th>
                  <th className="px-5 py-3 text-right">Devengado</th>
                  <th className="px-5 py-3 text-right">Pagado</th>
                  <th className="px-5 py-3 text-right">Disponible</th>
                  <th className="px-5 py-3 w-44">Ejecución</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.map((r) => {
                  const pct = r.techo > 0 ? (r.comprometido / r.techo) * 100 : 0;
                  return (
                    <tr key={r.id} className="transition-colors hover:bg-muted/50">
                      <td className="num px-5 py-3 font-semibold text-brand-700 dark:text-brand-300">{r.partida_codigo}</td>
                      <td className="px-5 py-3 text-ink">{r.partida_nombre}</td>
                      <td className="num px-5 py-3 text-right">{fmt.money(r.techo)}</td>
                      <td className="num px-5 py-3 text-right">{fmt.money(r.comprometido)}</td>
                      <td className="num px-5 py-3 text-right">{fmt.money(r.devengado)}</td>
                      <td className="num px-5 py-3 text-right">{fmt.money(r.pagado)}</td>
                      <td className={cn(
                        "num px-5 py-3 text-right font-semibold",
                        r.disponible < r.techo * 0.2 ? "text-amber-700" : "text-emerald-700"
                      )}>
                        {fmt.money(r.disponible)}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                            <div
                              className={cn(
                                "h-full transition-all",
                                pct < 50 ? "bg-emerald-500" :
                                pct < 80 ? "bg-amber-500" : "bg-rose-500"
                              )}
                              style={{ width: `${Math.min(100, pct)}%` }}
                            />
                          </div>
                          <span className="num text-[11px] tabular-nums text-ink-mute">{pct.toFixed(0)}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

function StatCard({ label, value, sub, accent }: {
  label: string; value: string; sub?: string; accent: 'brand'|'warn'|'success'|'neutral';
}) {
  const before = {
    brand: 'before:bg-brand-500',
    warn: 'before:bg-amber-500',
    success: 'before:bg-emerald-500',
    neutral: 'before:bg-ink-soft',
  }[accent];
  return (
    <div className={cn(
      "relative overflow-hidden rounded-lg border border-border bg-surface p-5 shadow-card",
      "before:absolute before:inset-x-0 before:top-0 before:h-[3px]", before
    )}>
      <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-mute">{label}</span>
      <div className="num mt-3 text-[22px] font-semibold text-ink">{value}</div>
      {sub && <div className="mt-1 text-[11px] text-ink-mute">{sub}</div>}
    </div>
  );
}

function LoadingState() {
  return <div className="flex h-64 items-center justify-center text-[13px] text-ink-mute">Cargando presupuesto…</div>;
}

function ErrorState({ msg }: { msg: string }) {
  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-center">
      <AlertCircle className="h-10 w-10 text-danger" />
      <p className="text-[14px] text-ink">Error cargando presupuesto</p>
      <p className="text-[12px] text-ink-mute">{msg}</p>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-center">
      <TrendingUp className="h-10 w-10 text-ink-soft" />
      <p className="text-[14px] text-ink">Sin presupuesto cargado todavía</p>
      <p className="text-[12px] text-ink-mute">Crea el presupuesto del ejercicio desde el CLI bootstrap o POST /api/presupuesto/egresos.</p>
      <CheckCircle2 className="hidden" />
    </div>
  );
}
